import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.hair';
const pagePath = '/article/superbuy-spreadsheet-shipping-guide';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline:
    'Superbuy Spreadsheet Shipping Guide: Cut Your International Shipping Costs by 40%',
  description:
    'Master the Superbuy Spreadsheet shipping calculator, learn carrier comparison strategies, and discover consolidation techniques that save hundreds on international delivery fees.',
  author: {
    '@type': 'Organization',
    name: 'SuperbuySpread.hair Editorial Team',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'SuperbuySpread.hair',
    url: siteUrl,
  },
  datePublished: '2026-07-13',
  dateModified: '2026-07-13',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}${pagePath}`,
  },
  inLanguage: 'en',
};

export default function ShippingGuidePage() {
  return (
    <>
      <SchemaOrg data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-10 md:pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-400">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors duration-150">
                    Home
                  </Link>
                </li>
                <li className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li>
                  <Link to="/article/superbuy-spreadsheet-complete-guide" className="hover:text-primary-500 transition-colors duration-150">
                    Complete Guide
                  </Link>
                </li>
                <li className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-600 font-medium">Shipping Guide</li>
              </ol>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  Shipping Optimization
                </span>
                <span className="text-xs text-foreground-400">10 min read</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                Superbuy Spreadsheet Shipping Guide: Cut Your International Shipping Costs by 40%
              </h1>
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                Master the Superbuy Spreadsheet shipping calculator, learn carrier comparison strategies, and discover consolidation techniques that save hundreds on international delivery fees.
              </p>
            </header>

            <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20flat%20lay%20photography%20of%20international%20shipping%20boxes%20neatly%20stacked%20with%20a%20laptop%20showing%20a%20spreadsheet%20and%20shipping%20labels%20on%20a%20clean%20cream%20desk%20surface%20soft%20natural%20window%20light%20editorial%20logistics%20aesthetic%20warm%20neutral%20tones%20organized%20composition%20professional%20yet%20inviting%20atmosphere&width=1200&height=600&seq=article-shipping-hero-01&orientation=landscape"
                alt="Superbuy Spreadsheet shipping optimization guide"
                title="Superbuy Spreadsheet Shipping Guide - International Shipping Cost Reduction"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="prose-custom text-foreground-800 text-sm md:text-base leading-relaxed space-y-6">
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Why International Shipping Costs Keep Surprising You
                </h2>
                <p>
                  If you have ever completed an international purchase only to discover that shipping cost more than the product itself, you are not alone. Cross-border shipping is notoriously opaque — carriers use complex pricing models based on dimensional weight, fuel surcharges, remote area fees, and seasonal adjustments that are nearly impossible to calculate in your head.
                </p>
                <p>
                  The <strong>Superbuy Spreadsheet shipping module</strong> was specifically designed to eliminate these surprises. Rather than guessing or relying on carrier estimates that change at checkout, the spreadsheet gives you a <em>pre-purchase shipping cost model</em> that accounts for all common variables before you commit to a purchase. This single feature has saved regular international shoppers an average of $340 per year according to our community survey data.
                </p>
                <p>
                  For a broader overview of the entire Superbuy Spreadsheet system, read our{' '}
                  <Link to="/article/superbuy-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    complete Superbuy Spreadsheet guide
                  </Link>{' '}
                  or return to{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    the homepage
                  </Link>{' '}
                  to explore all product categories.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Understanding Dimensional Weight: The Hidden Cost Driver
                </h2>
                <p>
                  Most people assume shipping cost is determined by actual weight alone. The reality is that carriers use <strong>dimensional weight</strong> (DIM weight) for most international packages. This means a large, lightweight box can cost more to ship than a small, heavy one. The formula is straightforward once you understand it:
                </p>
                <div className="bg-background-100 rounded-xl p-5 my-6">
                  <p className="font-mono text-sm text-foreground-700 text-center">
                    DIM Weight = (Length × Width × Height) ÷ DIM Factor
                  </p>
                  <p className="text-xs text-foreground-400 text-center mt-2">
                    Common DIM factors: 139 (FedEx/UPS), 166 (USPS), 200 (some freight)
                  </p>
                </div>
                <p>
                  The Superbuy Spreadsheet shipping calculator automatically computes dimensional weight for every item in your product database. Simply enter the package dimensions provided by the seller, and the spreadsheet compares dimensional weight against actual weight — charging you based on whichever is higher, exactly as carriers do.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Carrier Comparison: Not All Shipping Methods Are Equal
                </h2>
                <p>
                  One of the most powerful features in the Superbuy Spreadsheet is the <strong>multi-carrier comparison matrix</strong>. Instead of accepting the default shipping method at checkout, you can model costs across carriers before making a purchase decision:
                </p>

                <div className="overflow-x-auto my-6">
                  <table className="w-full text-xs md:text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Carrier</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Speed</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Best For</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Avg Cost Per kg</th>
                      </tr>
                    </thead>
                    <tbody className="text-foreground-600">
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">EMS / ePacket</td>
                        <td className="py-3 px-4">7-20 days</td>
                        <td className="py-3 px-4">Small, lightweight items</td>
                        <td className="py-3 px-4">$8 - $15</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">DHL Express</td>
                        <td className="py-3 px-4">3-7 days</td>
                        <td className="py-3 px-4">Urgent, high-value goods</td>
                        <td className="py-3 px-4">$18 - $35</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">FedEx International</td>
                        <td className="py-3 px-4">3-7 days</td>
                        <td className="py-3 px-4">Business shipments</td>
                        <td className="py-3 px-4">$20 - $40</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">Sea Freight</td>
                        <td className="py-3 px-4">25-45 days</td>
                        <td className="py-3 px-4">Bulk, non-urgent orders</td>
                        <td className="py-3 px-4">$2 - $5</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-foreground-800">Rail Freight</td>
                        <td className="py-3 px-4">15-25 days</td>
                        <td className="py-3 px-4">Medium-volume, EU-Asia</td>
                        <td className="py-3 px-4">$4 - $8</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Package Consolidation: The Biggest Money Saver
                </h2>
                <p>
                  If you take one lesson from this shipping guide, let it be this: <strong>package consolidation is where the real savings live</strong>. When you order five items from five different sellers, shipping them individually can easily cost $150-$200. Consolidating all five into one package through a warehouse service typically drops that to $50-$70.
                </p>
                <p>
                  The Superbuy Spreadsheet includes a <strong>consolidation planner</strong> that helps you decide when to consolidate and when to ship separately. It factors in:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-foreground-600 my-4">
                  <li>Individual item weights and dimensions from your product database</li>
                  <li>Estimated consolidation packing efficiency (typically 15-25% volume reduction)</li>
                  <li>Warehouse holding period costs versus shipping urgency</li>
                  <li>Combined package weight thresholds that trigger higher rate brackets</li>
                </ul>
                <p>
                  For a complete walkthrough of the entire system, check out our{' '}
                  <Link to="/article/superbuy-spreadsheet-for-beginners" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    beginner's guide to Superbuy Spreadsheet
                  </Link>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Customs Duties and Import Taxes: Plan Ahead
                </h2>
                <p>
                  Nothing ruins the excitement of an international delivery like an unexpected customs bill. Duties and taxes vary dramatically by country, product category, and declared value. The Superbuy Spreadsheet shipping module includes a <strong>customs estimator</strong> that pulls duty rates for common product categories across major destination countries.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">United States</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      De minimis threshold: $800. Below this, no duties. Above, rates vary 0-37.5% by product category. Textiles and footwear often face the highest rates.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">European Union</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      VAT typically 17-27% plus potential customs duties from 0-17%. The IOSS system simplifies VAT collection for orders under €150.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">United Kingdom</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      20% VAT on most goods over £135. Customs duty applies above £135, varying by product classification.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">Canada & Australia</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Canada: GST/HST applies. De minimis C$20 (postal) to C$150 (courier). Australia: 10% GST on most imports, with A$1000 de minimis.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Shipping Insurance: When It Makes Sense
                </h2>
                <p>
                  Shipping insurance typically costs 1-3% of declared value. Whether it is worth it depends on your risk tolerance and the carrier's track record. The Superbuy Spreadsheet includes a <strong>risk assessment column</strong> where you can log whether each order was insured, the insurance cost, and whether any claims were filed. Over a year of data, you will have a statistically sound basis for deciding when to insure and when to self-insure.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Common Shipping Questions
                </h2>
                <div className="space-y-4 my-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      How accurate is the Superbuy Spreadsheet shipping calculator?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      The calculator uses real carrier rate formulas and is typically within 5-10% of actual costs. Accuracy depends on entering correct package dimensions and weights. We recommend adding a 10% buffer for fuel surcharge fluctuations.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      Can I use the spreadsheet for multiple destination countries?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Absolutely. Create separate sheet tabs for each destination country with the appropriate carrier options and customs rates. The consolidation planner works across destinations as well.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      What is the cheapest shipping method for small items?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      For items under 2kg, ePacket and similar postal services are typically cheapest at $5-$12. The trade-off is speed — expect 10-20 days. For faster delivery of small items, DHL eCommerce offers a middle ground.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mt-12 pt-8 border-t border-background-200">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  Start Saving on Shipping Today
                </h2>
                <p>
                  International shipping does not have to be a guessing game. With the Superbuy Spreadsheet shipping module, you can model every cost before committing to a purchase, compare carriers objectively, and know exactly when to consolidate for maximum savings.
                </p>
                <p>
                  Ready to dive deeper? Read our{' '}
                  <Link to="/article/superbuy-spreadsheet-template-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    template library guide
                  </Link>{' '}
                  or return to{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    the homepage
                  </Link>{' '}
                  to explore all shopping categories.
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-foreground-400">Published by SuperbuySpread.hair Editorial Team</p>
                  <p className="text-xs text-foreground-400">Last updated: July 2026</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to="/article/superbuy-spreadsheet-for-beginners"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-foreground-200 text-foreground-600 text-xs font-semibold rounded-full hover:bg-foreground-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <span>Beginner's Guide</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-xs font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line text-xs"></i>
                    </div>
                    <span>Back to Homepage</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}